import React, { useEffect, useState } from 'react';
import { Gift, Save, Plus, Trash2, Calendar, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface SiteSettings {
  id: number;
  free_chat_credits: number;
  free_voice_minutes: number;
  enable_free_chat: boolean;
  enable_free_voice: boolean;
  updated_at: string;
}

interface PromotionalCredit {
  id: string;
  promo_chat_credits: number | null;
  promo_voice_minutes: number | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
  created_at: string;
}

export default function CreditsManager() {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [promotions, setPromotions] = useState<PromotionalCredit[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoForm, setPromoForm] = useState({
    promo_chat_credits: '',
    promo_voice_minutes: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [settingsResult, promosResult] = await Promise.all([
        supabase.from('site_settings').select('*').eq('id', 1).single(),
        supabase.from('promotional_credits').select('*').order('created_at', { ascending: false })
      ]);

      if (settingsResult.error) throw settingsResult.error;
      if (promosResult.error) throw promosResult.error;

      setSettings(settingsResult.data);
      setPromotions(promosResult.data || []);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { error: updateError } = await supabase
        .from('site_settings')
        .update({
          free_chat_credits: settings.free_chat_credits,
          free_voice_minutes: settings.free_voice_minutes,
          enable_free_chat: settings.enable_free_chat,
          enable_free_voice: settings.enable_free_voice
        })
        .eq('id', 1);

      if (updateError) throw updateError;

      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const createPromotion = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (!promoForm.start_date || !promoForm.end_date) {
        setError('Please set start and end dates');
        return;
      }

      if (!promoForm.promo_chat_credits && !promoForm.promo_voice_minutes) {
        setError('Please set at least one credit type');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: deactivateError } = await supabase
        .from('promotional_credits')
        .update({ is_active: false })
        .eq('is_active', true);

      if (deactivateError) throw deactivateError;

      const { error: insertError } = await supabase
        .from('promotional_credits')
        .insert({
          promo_chat_credits: promoForm.promo_chat_credits ? parseInt(promoForm.promo_chat_credits) : null,
          promo_voice_minutes: promoForm.promo_voice_minutes ? parseFloat(promoForm.promo_voice_minutes) : null,
          start_date: promoForm.start_date,
          end_date: promoForm.end_date,
          is_active: true,
          created_by: user.id
        });

      if (insertError) throw insertError;

      setSuccess('Promotion created successfully!');
      setShowPromoForm(false);
      setPromoForm({
        promo_chat_credits: '',
        promo_voice_minutes: '',
        start_date: '',
        end_date: ''
      });

      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error creating promotion:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deletePromotion = async (id: string) => {
    if (!confirm('Are you sure you want to delete this promotion?')) return;

    try {
      setSaving(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('promotional_credits')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setSuccess('Promotion deleted successfully!');
      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error deleting promotion:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePromoStatus = async (id: string, currentStatus: boolean) => {
    try {
      setSaving(true);
      setError(null);

      if (!currentStatus) {
        await supabase
          .from('promotional_credits')
          .update({ is_active: false })
          .eq('is_active', true);
      }

      const { error: updateError } = await supabase
        .from('promotional_credits')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (updateError) throw updateError;

      setSuccess(`Promotion ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error toggling promotion:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const isPromoActive = (promo: PromotionalCredit) => {
    const now = new Date();
    const start = new Date(promo.start_date);
    const end = new Date(promo.end_date);
    return promo.is_active && now >= start && now <= end;
  };

  const getPromoStatus = (promo: PromotionalCredit) => {
    const now = new Date();
    const start = new Date(promo.start_date);
    const end = new Date(promo.end_date);

    if (!promo.is_active) return { text: 'Inactive', color: 'bg-gray-500' };
    if (now < start) return { text: 'Scheduled', color: 'bg-blue-500' };
    if (now > end) return { text: 'Expired', color: 'bg-red-500' };
    return { text: 'Active', color: 'bg-green-500' };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Gift className="w-6 h-6 text-amber-600 mr-2" />
            <h2 className="text-2xl font-bold text-gray-800">Default Signup Credits</h2>
          </div>
        </div>

        {settings && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      Chat Credits
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={settings.free_chat_credits}
                      onChange={(e) => setSettings({ ...settings, free_chat_credits: parseInt(e.target.value) || 0 })}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      disabled={!settings.enable_free_chat}
                    />
                  </div>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.enable_free_chat}
                      onChange={(e) => setSettings({ ...settings, enable_free_chat: e.target.checked })}
                      className="w-5 h-5 text-amber-600 rounded focus:ring-2 focus:ring-amber-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Enabled</span>
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      Voice Minutes
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={settings.free_voice_minutes}
                      onChange={(e) => setSettings({ ...settings, free_voice_minutes: parseFloat(e.target.value) || 0 })}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      disabled={!settings.enable_free_voice}
                    />
                  </div>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.enable_free_voice}
                      onChange={(e) => setSettings({ ...settings, enable_free_voice: e.target.checked })}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Enabled</span>
                  </label>
                </div>
              </div>
            </div>

            <button
              onClick={saveSettings}
              disabled={saving}
              className="flex items-center px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition disabled:opacity-50"
            >
              <Save className="w-5 h-5 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Calendar className="w-6 h-6 text-purple-600 mr-2" />
            <h2 className="text-2xl font-bold text-gray-800">Promotional Campaigns</h2>
          </div>
          <button
            onClick={() => setShowPromoForm(!showPromoForm)}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Promotion
          </button>
        </div>

        {showPromoForm && (
          <div className="mb-6 p-6 bg-purple-50 rounded-lg border-2 border-purple-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Create Promotion</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Promo Chat Credits
                </label>
                <input
                  type="number"
                  min="0"
                  value={promoForm.promo_chat_credits}
                  onChange={(e) => setPromoForm({ ...promoForm, promo_chat_credits: e.target.value })}
                  placeholder="Leave empty to skip"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Promo Voice Minutes
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={promoForm.promo_voice_minutes}
                  onChange={(e) => setPromoForm({ ...promoForm, promo_voice_minutes: e.target.value })}
                  placeholder="Leave empty to skip"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={promoForm.start_date}
                  onChange={(e) => setPromoForm({ ...promoForm, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={promoForm.end_date}
                  onChange={(e) => setPromoForm({ ...promoForm, end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={createPromotion}
                disabled={saving}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
              >
                <Save className="w-5 h-5 mr-2" />
                {saving ? 'Creating...' : 'Create Promotion'}
              </button>
              <button
                onClick={() => setShowPromoForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {promotions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No promotions created yet</p>
          ) : (
            promotions.map((promo) => {
              const status = getPromoStatus(promo);
              return (
                <div key={promo.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`px-3 py-1 ${status.color} text-white text-xs font-semibold rounded-full`}>
                          {status.text}
                        </span>
                        <div className="text-sm text-gray-600">
                          {promo.promo_chat_credits && (
                            <span className="mr-4">💬 {promo.promo_chat_credits} messages</span>
                          )}
                          {promo.promo_voice_minutes && (
                            <span>🎤 {promo.promo_voice_minutes} minutes</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center text-sm text-gray-600 space-x-4">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(promo.start_date).toLocaleString()}
                        </div>
                        <span>→</span>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {new Date(promo.end_date).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => togglePromoStatus(promo.id, promo.is_active)}
                        disabled={saving}
                        className={`px-3 py-1 ${promo.is_active ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'} text-white text-sm rounded-lg transition disabled:opacity-50`}
                      >
                        {promo.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => deletePromotion(promo.id)}
                        disabled={saving}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
